import React from 'react';

import { cssMap } from '@atlaskit/css';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { Box } from '@atlaskit/primitives/compiled';

export const CURSOR_DROP_INDICATOR_OFFSET = '--editor-cursor-drop-indicator-offset';
export const CURSOR_DROP_INDICATOR_GAP = '--editor-cursor-drop-indicator-gap';

const styles = cssMap({
	/**
	 * Absolutely positioned within the widget host so the indicator
	 * overlays the block boundary without affecting document layout.
	 * `left: 0; right: 0` (rather than a fixed `width`) makes the
	 * indicator stretch to the FULL width of its offset parent — which
	 * is the widget host element. The widget host itself is rendered
	 * inside whichever container ProseMirror placed the decoration in
	 * (the document root, a `layoutColumn`, a hub `section` content
	 * frame, a `bodiedExtension` body, etc.) and naturally takes the
	 * width of that container.
	 *
	 * Result: the indicator is exactly as wide as the container the
	 * user is dropping into — never wider, never narrower — without
	 * any JS measurement, `ResizeObserver`, or per-frame width
	 * recalculation. This is critical inside `layoutSection`s where
	 * each `layoutColumn` can have a different width.
	 */
	container: {
		position: 'absolute',
		left: 0,
		right: 0,
		display: 'block',
		zIndex: 100,
		transform: `translateY(var(${CURSOR_DROP_INDICATOR_OFFSET}, 0))`,
	},
	dropIndicator: {
		height: '100%',
		width: '100%',
		position: 'relative',
		display: 'block',
	},
});

/**
 * Visual indicator only – no drop handling. Drop is handled by
 * `EditorDropTarget` in `hub-home`.
 *
 * The indicator's width is inherited from its offset parent (the
 * widget host `<div>` created by `createCursorIndicatorDecoration`),
 * which is in turn placed by ProseMirror inside the natural container
 * the cursor is over. No `lineLength` / fixed pixel width is required.
 */
export function CursorDropIndicator(): React.JSX.Element {
	return (
		<Box xcss={styles.container} testId="hub-external-insert-cursor-drop-indicator">
			<Box xcss={styles.dropIndicator} testId="hub-external-insert-cursor-drop-indicator-box">
				<DropIndicator edge="bottom" />
			</Box>
		</Box>
	);
}
